import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";

// Local imports
import { BrokerListWrapper } from "./style/Style";
import AddBroker from "./components/AddBroker";
import MetricsModal from "./components/MetricsModal";
import BrokerTable from "./components/BrokerTable";

// Tab configuration
const TABS = [
    { id: "sell_direct", label: "Sell my car (Direct Offer)" },
    { id: "sell_brokerage", label: "Sell my car (Brokerage)" },
    { id: "buy", label: "Buy a car" },
    { id: "insurance", label: "Car insurance" },
    { id: "finance", label: "Car Financing" },
];

// Utility Functions
/**
 * Formats lead quota display string
 * @param {Object} broker - Broker data object
 * @returns {string} Formatted quota string or "-"
 */
const formatLeadQuota = (broker) => {
    const daily = `${broker.daily_lead_used || "0"} / ${broker.daily_lead_quota || "-"}`;
    const monthly = `${broker.monthly_lead_used || "0"} / ${broker.monthly_lead_quota || "-"}`;

    return (
        <>
            {daily}
            <br />
            {monthly}
        </>
    );
};

/**
 * Gets broker name from various possible fields
 * @param {Object} broker - Broker data object
 * @returns {string} Broker name or "-"
 */
const getBrokerName = (broker) => {
    return broker.company_name || broker.broker_name || broker.name || "-";
};

/**
 * Gets status indicator JSX element
 * @param {Object} broker - Broker data object
 * @returns {JSX.Element} Status indicator span
 */
const getStatusIndicator = (broker) => {
    const isActive = broker.is_active === true;

    return isActive ? (
        <span className="status status-active" title="Active"></span>
    ) : (
        <span className="status status-end" title="Inactive"></span>
    );
};

/**
 * Checks if the order of items has changed compared to original data
 * @param {Array} items - Current items array
 * @param {Array} data - Original data array
 * @returns {boolean} True if order has changed
 */
const hasOrderChanged = (items, data) => {
    return JSON.stringify(items) !== JSON.stringify(data);
};

// Export utility functions for use in BrokerRow component and BrokerTable
export { formatLeadQuota, getBrokerName, getStatusIndicator, hasOrderChanged };

const BrokerManagement = () => {

    const userAccess = JSON.parse(localStorage.getItem("permissions"));

    const search = useLocation().search;
    const [searchParams, setSearchParams] = useSearchParams();

    // Memoize query to avoid recreating on every render
    const query = React.useMemo(() => new URLSearchParams(search), [search]);

    const brokerManagement = React.useMemo(() => userAccess.find((o) => o.slug === "leads_management"), [userAccess]);

    const canAddBroker = React.useMemo(() => brokerManagement?.permissions?.find((o) => o.slug === "add"), [brokerManagement]);
    const canEditBroker = React.useMemo(() => brokerManagement?.permissions?.find((o) => o.slug === "update"), [brokerManagement]);
    const canDeleteBroker = React.useMemo(() => brokerManagement?.permissions?.find((o) => o.slug === "delete"), [brokerManagement]);

    // Get initial tab from URL query parameter, default to first tab
    const getInitialTab = () => {
        const typeParam = query.get("type");
        if (typeParam && TABS.find(tab => tab.id === typeParam)) {
            return typeParam;
        }
        return TABS[0].id;
    };

    const [activeTab, setActiveTab] = useState(getInitialTab());
    const [editingBroker, setEditingBroker] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Update tab when URL query parameter changes
    useEffect(() => {
        const typeParam = query.get("type");
        if (typeParam && TABS.find(tab => tab.id === typeParam) && typeParam !== activeTab) {
            setActiveTab(typeParam);
        } else if (!typeParam && activeTab !== TABS[0].id) {
            setActiveTab(TABS[0].id);
        }
    }, [search, activeTab, query]);

    /**
     * Handles tab change
     */
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        // Update URL query parameter
        searchParams.set("type", tabId);
        setSearchParams(searchParams);

        // Clear other query params when switching tabs
        for (const value of query.keys()) {
            if (value !== "type") {
                searchParams.delete(value);
            }
        }
        setSearchParams(searchParams);
    };

    /**
     * Handles edit broker callback
     */
    const handleEditBroker = (broker) => {
        setEditingBroker(broker.id);
    };

    /**
     * Handles broker update success
     */
    const handleBrokerUpdateSuccess = () => {
        setEditingBroker(null);
        // Trigger refresh by updating refreshTrigger
        setRefreshTrigger(prev => prev + 1);
    };

    /**
     * Handles edit broker close
     */
    const handleEditClose = () => {
        setEditingBroker(null);
    };

    return (
        <BrokerListWrapper>
            <div className="auctionMainContainer">
                <div className="row mt-4">
                    <div className="col-md-4 offset-md-4">
                        <h1 className="text-center">BROKER MANAGEMENT</h1>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="row mt-2">
                    <div className="col-md-12 d-flex justify-content-center">
                        <div className="tabBtn d-flex">
                            {TABS.map((tab) => (
                                <button
                                    key={tab.id}
                                    className={activeTab === tab.id ? "activeTab" : ""}
                                    onClick={() => handleTabChange(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex align-items-center justify-content-between add-info mt-2">
                    <div className="d-flex justify-content-end mb-2 data-button gap-2">
                        {canAddBroker && (
                            <AddBroker onSuccess={handleBrokerUpdateSuccess} />
                        )}
                        {editingBroker && canEditBroker && (
                            <AddBroker
                                brokerId={editingBroker}
                                isEdit={true}
                                onSuccess={handleBrokerUpdateSuccess}
                                onClose={handleEditClose}
                            />
                        )}
                        <MetricsModal />
                        <NavLink to={`/broker-management/unsold-leads?type=${activeTab}`} className="btn">Unsold Leads</NavLink>
                    </div>
                </div>

                {/* Broker Table Component */}
                <BrokerTable
                    type={activeTab}
                    onEdit={handleEditBroker}
                    refreshTrigger={refreshTrigger}
                    canDeleteBroker={canDeleteBroker}
                    canEditBroker={canEditBroker}
                />
            </div>
        </BrokerListWrapper>
    );
};

export default BrokerManagement;
